import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import Papaparse, { ParseError, ParseResult } from 'papaparse';
import { useDropzone } from 'react-dropzone';
import { ReactComponent as CSVIcon } from 'assets/icons/csv-file.svg';
import './CSVDropParser.pcss';
import { useSelector } from 'react-redux';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { selectCurrentUserIsAdmin } from '@mm/state/selectors/userSelector';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';

type Props = {
  className?: string;
  onSuccess?: (file: File, parsedFile: IParsedData) => void;
  onFileReject?: (rejected: File[]) => void;
  onParseFail?: (file: File, parseErr?: ParseError) => void;
  onFileRemove?: Function;
  isFirstRowHeader?: boolean;
  onHeaderCheckboxToggle?: (isEnabled: boolean) => void;
  hideFirstRowDescription?: boolean;
  showUploadBtn?: boolean;
  showCsvIcon?: boolean;
  showCheckIcon?: boolean;
  customText?: string;
  customCSVIcon?: React.ReactNode;
  customCloseIcon?: React.ReactNode;
  customFile?: File;
  customSetFile?: (file?: File) => void;
};

const accept = '.csv';

export type IParsedData = ParseResult;

const parseCsvFile = (file: File, header: boolean): Promise<IParsedData> =>
  new Promise((resolve, reject) => {
    Papaparse.parse(file, {
      header,
      skipEmptyLines: true,
      complete: data => resolve(data),
      error: err => reject(err),
    });
  });

export const CSVDropParser = ({
  className,
  onSuccess,
  onFileReject,
  onParseFail,
  onFileRemove,
  isFirstRowHeader = false,
  onHeaderCheckboxToggle,
  hideFirstRowDescription,
  showUploadBtn,
  showCsvIcon,
  customText,
  customCSVIcon,
  customCloseIcon,
  showCheckIcon,
  customFile,
  customSetFile,
}: Props) => {
  const [file, setFile] = useState<File | undefined>(customFile);
  const [isError, setIsError] = useState(false);
  const isSysOrCSAdmin = useSelector(selectCurrentUserIsAdmin);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const maxSize =
    (isSysOrCSAdmin ? 10 : facebookPage?.free ? 2 : 5) * 1024 * 1024;

  useEffect(() => {
    setFile(customFile);
  }, [customFile]);

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    maxSize,
    onDrop: async (files: File[], rejectedFiles: File[]) => {
      if (files.length) {
        setIsError(false);
        const file = files[0];
        setFile(file);
        customSetFile?.(file);

        try {
          const parsed = await parseCsvFile(file, isFirstRowHeader);
          onSuccess && onSuccess(file, parsed);
        } catch (err) {
          onParseFail && onParseFail(file, err);
        }
      } else if (rejectedFiles.length) {
        setIsError(false);
        onFileReject && onFileReject(rejectedFiles);
      }
    },
  });

  const onTrashIconClick = () => {
    setIsError(false);
    setFile(undefined);
    customSetFile?.(undefined);
    onFileRemove && onFileRemove();
  };

  const onFirstRowHeaderToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onHeaderCheckboxToggle && onHeaderCheckboxToggle(e.target.checked);
  };
  const containerProps = showUploadBtn
    ? { ...getRootProps(), onClick: undefined }
    : { ...getRootProps() };
  return (
    <div className={classNames(className, 'csv-drop-parser')}>
      {!hideFirstRowDescription && (
        <div className="csv-drop-parser__checkbox-container">
          <input
            type="checkbox"
            className="csv-drop-parser__checkbox"
            disabled={true}
            checked={isFirstRowHeader}
            onChange={onFirstRowHeaderToggle}
          />
          <span>First row contains column headers</span>
        </div>
      )}

      {file ? (
        <div className="csv-drop-parser__file-row">
          {customCSVIcon || <CSVIcon className="csv-drop-parser__csv-icon" />}
          <div className="csv-drop-parser__file-row-body">
            <span className="csv-drop-parser__file-name">{file.name}</span>
            <div className="csv-drop-parser__icons">
              <span
                className="csv-drop-parser__trash-icon"
                onClick={onTrashIconClick}
              >
                {customCloseIcon || <DeleteOutlined />}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="csv-drop-parser__drop-area" {...containerProps}>
          {customCSVIcon ||
            (!showUploadBtn && (
              <CSVIcon className="csv-drop-parser__csv-icon" />
            ))}

          <div className="csv-drop-parser__text">
            {customText || (
              <>
                Drop or
                <span className="csv-drop-parser__action-text">
                  choose file
                </span>
              </>
            )}
            {showUploadBtn && (
              <div {...getRootProps()} className="csv-drop-parser__upload-btn">
                <UploadOutlined className="csv-drop-parser__upload-btn-icon" />{' '}
                Upload
              </div>
            )}
          </div>

          <input {...getInputProps()} accept={accept} />
        </div>
      )}
    </div>
  );
};
